import { ApolloClient, InMemoryCache, createHttpLink, from, Observable } from '@apollo/client';
import type { NormalizedCacheObject } from '@apollo/client';
import { onError } from '@apollo/client/link/error';
import { LocalStorageWrapper, SynchronousCachePersistor } from 'apollo3-cache-persist';
import { REFRESH_TOKEN_MUTATION } from '../graphql/mutations';

const httpLink = createHttpLink({
  uri: import.meta.env.VITE_GRAPHQL_ENDPOINT || '/graphql',
  credentials: 'include',
});

let isRedirecting = false;
let isRefreshing = false;
let pendingRequests: (() => void)[] = [];

const resolvePendingRequests = () => {
  pendingRequests.forEach((callback) => callback());
  pendingRequests = [];
};

const errorLink = onError(({ graphQLErrors, networkError, operation, forward }) => {
  if (graphQLErrors) {
    for (const error of graphQLErrors) {
      const { message, locations, path, extensions } = error;
      console.error(`[GraphQL error]: Message: ${message}, Location: ${locations}, Path: ${path}`);

      const operationName = operation.operationName;
      const isAuthOperation = [
        'Login',
        'Logout',
        'RefreshToken',
        'RequestPasswordReset',
        'ResetPassword',
      ].includes(operationName);

      if (extensions?.code === 'UNAUTHENTICATED' && !isAuthOperation) {
        if (
          window.location.pathname === '/login' ||
          window.location.pathname === '/forgot-password' ||
          window.location.pathname.startsWith('/reset-password')
        ) {
          return;
        }

        // Try to refresh the token
        if (!isRefreshing) {
          isRefreshing = true;

          return new Observable((observer) => {
            // Attempt to refresh the token
            apolloClient
              .mutate({ mutation: REFRESH_TOKEN_MUTATION })
              .then(() => {
                isRefreshing = false;
                resolvePendingRequests();
                // Retry the failed request
                const subscriber = {
                  next: observer.next.bind(observer),
                  error: observer.error.bind(observer),
                  complete: observer.complete.bind(observer),
                };
                forward(operation).subscribe(subscriber);
              })
              .catch(() => {
                isRefreshing = false;
                pendingRequests = [];
                // Refresh failed, redirect to login
                if (!isRedirecting) {
                  isRedirecting = true;
                  setTimeout(() => {
                    window.location.href = '/login';
                  }, 100);
                }
                observer.error(error);
              });
          });
        } else {
          // Already refreshing, queue this request
          return new Observable((observer) => {
            pendingRequests.push(() => {
              const subscriber = {
                next: observer.next.bind(observer),
                error: observer.error.bind(observer),
                complete: observer.complete.bind(observer),
              };
              forward(operation).subscribe(subscriber);
            });
          });
        }
      }
    }
  }

  if (networkError) {
    console.error(`[Network error]: ${networkError}`);
  }
});

const cache = new InMemoryCache({
  typePolicies: {
    Query: {
      fields: {
        personalTasks: {
          merge(_, incoming) {
            return incoming;
          },
        },
        entries: {
          merge(_, incoming) {
            return incoming;
          },
        },
      },
    },
    User: {
      fields: {
        families: {
          merge(_, incoming) {
            return incoming;
          },
        },
        personalTasks: {
          merge(_, incoming) {
            return incoming;
          },
        },
      },
    },
    Family: {
      fields: {
        members: {
          merge(_, incoming) {
            return incoming;
          },
        },
        entries: {
          merge(_, incoming) {
            return incoming;
          },
        },
      },
    },
  },
});

/**
 * Persist the normalized Apollo cache to localStorage so documents READ
 * offline: the PWA shell is already precached, so with the persisted cache a
 * reload without network still renders every page from data
 * (`cache-and-network` + `previousData` everywhere) and revalidates when
 * connectivity returns. Restore is synchronous so the first render already
 * sees the data. Failure (private mode, quota) degrades to the old
 * online-only behavior — never a throw at startup.
 */
const APOLLO_CACHE_KEY = 'alma-apollo-cache';
let persistor: SynchronousCachePersistor<NormalizedCacheObject> | null = null;
try {
  persistor = new SynchronousCachePersistor({
    cache,
    storage: new LocalStorageWrapper(window.localStorage),
    key: APOLLO_CACHE_KEY,
    maxSize: 4 * 1024 * 1024,
  });
  persistor.restoreSync();
} catch (err) {
  console.error('[offline] cache persistence unavailable:', err);
}

/**
 * Drop the persisted snapshot (logout) so no document text outlives the
 * session in browser storage. The persistor stays active — post-logout cache
 * writes (`me: null`) re-persist harmlessly.
 */
export function purgePersistedCache(): void {
  try {
    persistor?.remove();
    window.localStorage.removeItem(APOLLO_CACHE_KEY);
  } catch {
    // Storage unavailable — nothing persisted to purge.
  }
}

/** Apollo Client instance with auth error handling and token refresh. */
export const apolloClient = new ApolloClient({
  link: from([errorLink, httpLink]),
  cache,
  defaultOptions: {
    watchQuery: {
      fetchPolicy: 'cache-and-network',
    },
  },
});
